import { BusinessOnboardingStepKey, IBusinessOnboardStep } from '@query'
import { MarkdownText } from '@root/containers/MarkdownText'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaCheckCircle } from 'react-icons/fa'
import styled from 'styled-components'
import { StepRow } from './StepRow'
import type { GetStartedBankReturnKind } from './useBankHandover'
import {
  didCompleteAutomationSteps,
  getActiveStep,
  getGroupStatus,
  isGroupHandled,
  isLegacyOnboard
} from './companionStepState'

interface Props {
  steps: IBusinessOnboardStep[]
  earned: number
  showSuccessfulBankReturn: boolean
  bankReturnKind: GetStartedBankReturnKind | null
  allDone: boolean
  burstGroupKey: string | null
  onBankCta: () => void
  onGoToTransactions: () => void
  onResolved: (groupKey: IBusinessOnboardStep['group_key'], kind: 'done' | 'skipped') => void
  onCollapse: () => void
}

export const CompanionBody: React.FC<Props> = ({
  steps,
  earned,
  showSuccessfulBankReturn,
  bankReturnKind,
  allDone,
  burstGroupKey,
  onBankCta,
  onGoToTransactions,
  onResolved,
  onCollapse
}) => {
  const [t] = useTranslation()
  const defaultOpenKey = showSuccessfulBankReturn
    ? BusinessOnboardingStepKey.BANK_INTEGRATION
    : getActiveStep(steps)?.group_key ?? null
  const [selectedGroupKey, setSelectedGroupKey] = React.useState<
    IBusinessOnboardStep['group_key'] | null | undefined
  >(undefined)
  const selectedStep = steps.find(step => step.group_key === selectedGroupKey)
  if (
    !showSuccessfulBankReturn &&
    selectedGroupKey &&
    burstGroupKey === selectedGroupKey &&
    selectedStep &&
    isGroupHandled(selectedStep)
  ) {
    setSelectedGroupKey(undefined)
  }
  const openGroupKey = selectedGroupKey === undefined ? defaultOpenKey : selectedGroupKey

  const onToggle = (groupKey: IBusinessOnboardStep['group_key']) => {
    setSelectedGroupKey(current => {
      const openKey = current === undefined ? defaultOpenKey : current
      return openKey === groupKey ? null : groupKey
    })
  }

  const legacyOnboard = isLegacyOnboard(steps)
  const automationDone = didCompleteAutomationSteps(steps)
  const allDoneTitleKey = legacyOnboard
    ? 'allDoneTitleLegacy'
    : earned > 0
    ? 'allDoneTitle'
    : 'allDoneTitleNoCredits'
  const allDoneBodyKey = legacyOnboard
    ? 'allDoneBodyLegacy'
    : automationDone
    ? 'allDoneBodyAutomation'
    : earned > 0
    ? 'allDoneBodyCredits'
    : 'allDoneBodyPlain'

  return (
    <Body>
      {allDone && (
        <AllDone>
          <DoneIcon />
          <AllDoneTitle>
            {earned > 0 && !legacyOnboard
              ? t(`dashboard.getStartedWidget.${allDoneTitleKey}`, { credits: earned })
              : t(`dashboard.getStartedWidget.${allDoneTitleKey}`)}
          </AllDoneTitle>
          <AllDoneBody>{t(`dashboard.getStartedWidget.${allDoneBodyKey}`)}</AllDoneBody>
          {!legacyOnboard && (
            <AllDoneHelp>
              <MarkdownText text={t('dashboard.getStartedWidget.allDoneStillHaveQuestions')} />
            </AllDoneHelp>
          )}
        </AllDone>
      )}

      {!allDone && (
        <List>
          {steps.map(step => (
            <StepRow
              key={step.group_key}
              step={step}
              status={getGroupStatus(step)}
              isOpen={step.group_key === openGroupKey}
              isBursting={burstGroupKey === step.group_key}
              onToggle={() => onToggle(step.group_key)}
              onBankCta={onBankCta}
              onGoToTransactions={onGoToTransactions}
              showBankSuccess={
                showSuccessfulBankReturn &&
                step.group_key === BusinessOnboardingStepKey.BANK_INTEGRATION
              }
              bankReturnKind={bankReturnKind}
              onResolved={onResolved}
              onCollapse={onCollapse}
            />
          ))}
        </List>
      )}
    </Body>
  )
}

const Body = styled.div`
  padding: ${({ theme }) => theme.spacing.lg}rem;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
`

const AllDone = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  border: 2px solid ${({ theme }) => theme.colors.success};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  padding: ${({ theme }) => theme.spacing.lg}rem;
`

const DoneIcon = styled(FaCheckCircle)`
  font-size: 2.2rem;
  color: ${({ theme }) => theme.colors.success};
`

const AllDoneTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.lg}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.menuBg};
`

const AllDoneBody = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
`

const AllDoneHelp = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};

  & * {
    margin-bottom: 0;
  }
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
`
